/**
 * Shared sort configuration for all report tabs.
 *
 * Defines SortableColumn definitions (used by buildSortOptions to generate i18n'd dropdown options),
 * default SortConfig per tab type, and comparator functions (used by applySort inside useFilteredData).
 */
import { SortableColumn, SortDirection } from '../../../../utils/list-filters';

// ─── Column definitions ─────────────────────────────────────────────────────

export const DATE_CREATED_SORT_COLUMN: SortableColumn = {
  key: 'dateCreated',
  labelStyle: 'date',
  columnNameId: 'common.date_created',
  columnNameDefaultMessage: 'Date created'
};

export const NAME_SORT_COLUMN: SortableColumn = {
  key: 'name',
  labelStyle: 'alpha',
  columnNameId: 'common.name',
  columnNameDefaultMessage: 'Name'
};

export const SENDING_SORT_COLUMN: SortableColumn = {
  key: 'sending',
  labelStyle: 'date',
  columnNameId: 'common.sending',
  columnNameDefaultMessage: 'Sending'
};

/** Sort columns for My Reports, Report Templates, and Exported PDFs */
export const REPORTS_SORT_COLUMNS: SortableColumn[] = [DATE_CREATED_SORT_COLUMN, NAME_SORT_COLUMN];

/** Sort columns for Report Scheduling */
export const SCHEDULING_SORT_COLUMNS: SortableColumn[] = [SENDING_SORT_COLUMN, NAME_SORT_COLUMN];

// ─── Default sort configs ────────────────────────────────────────────────────

export const DEFAULT_REPORTS_SORT = { column: 'dateCreated', direction: SortDirection.DESC };
export const DEFAULT_SCHEDULING_SORT = { column: 'sending', direction: SortDirection.ASC };

// ─── Comparators ─────────────────────────────────────────────────────────────

export interface HasName {
  readonly name: string;
}

export interface HasCreated {
  readonly created: string;
}

interface HasNextGenerateAt {
  readonly next_generate_at: string | null;
}

/** Alphabetical comparison by item name */
export const nameComparator = <T extends HasName>(a: T, b: T): number => {
  return a.name.localeCompare(b.name);
};

/** Chronological comparison by created date */
export const dateCreatedComparator = <T extends HasCreated>(a: T, b: T): number => {
  return new Date(a.created).getTime() - new Date(b.created).getTime();
};

/** Chronological comparison by next_generate_at. Null values are pushed to the end. */
export const sendingComparator = <T extends HasNextGenerateAt>(a: T, b: T): number => {
  if (a.next_generate_at === null && b.next_generate_at === null) {
    return 0;
  }
  if (a.next_generate_at === null) {
    return 1;
  }
  if (b.next_generate_at === null) {
    return -1;
  }
  return new Date(a.next_generate_at).getTime() - new Date(b.next_generate_at).getTime();
};

/**
 * Comparators for My Reports / Report Templates / Exported PDFs.
 * Keys must match the `key` field of the corresponding SortableColumn definitions.
 * Used by applySort() inside useFilteredData to order items by the selected column.
 */
export const REPORTS_COMPARATORS: Record<string, (a: HasName & HasCreated, b: HasName & HasCreated) => number> = {
  dateCreated: dateCreatedComparator,
  name: nameComparator
};

/**
 * Comparators for Report Scheduling.
 * Keys must match the `key` field of SCHEDULING_SORT_COLUMNS.
 */
export const SCHEDULING_COMPARATORS: Record<
  string,
  (a: HasName & HasNextGenerateAt, b: HasName & HasNextGenerateAt) => number
> = {
  sending: sendingComparator,
  name: nameComparator
};

// ─── localStorage keys ───────────────────────────────────────────────────────

export const MY_REPORTS_STORAGE_KEY = 'reports-my-reports-filter-v1';
export const REPORT_TEMPLATES_STORAGE_KEY = 'reports-templates-filter-v1';
export const FUTRLI_TEMPLATES_STORAGE_KEY = 'reports-futrli-templates-filter-v1';
export const REPORT_SCHEDULING_STORAGE_KEY = 'reports-scheduling-filter-v1';
export const EXPORTED_PDFS_STORAGE_KEY = 'reports-exported-pdfs-filter-v1';
